import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Wallet,
  MessageSquare,
  LogOut,
  ChevronLeft,
  Globe,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useApp } from '@/contexts/AppContext';
import { useUserAuth } from '@/contexts/UserAuthContext';
import { useActiveCountries } from '@/hooks/useAppData';
import { cn } from '@/lib/utils';
import { StoreRequestSheet } from '@/components/account/StoreRequestSheet';

// ─── Account section helper ─────────────────────────────────────────────────

interface MenuItem {
  icon: React.ElementType;
  label: string;
  sublabel?: string;
  action: () => void;
  danger?: boolean;
}

function AccountSection({
  title,
  items,
  hideTitle = false,
}: {
  title: string;
  items: MenuItem[];
  hideTitle?: boolean;
}) {
  return (
    <div className="px-4 mb-4">
      {!hideTitle && (
        <p className="text-xs font-semibold text-muted-foreground mb-2 px-1">{title}</p>
      )}
      <div className="bg-card rounded-2xl overflow-hidden border border-border">
        {items.map((item, index) => {
          const Icon = item.icon;
          return (
            <button
              key={item.label}
              onClick={item.action}
              className={cn(
                'flex items-center gap-3 w-full p-4 text-right transition-colors active:bg-secondary/60',
                index !== items.length - 1 && 'border-b border-border',
                item.danger && 'text-destructive',
              )}
            >
              <div
                className={cn(
                  'w-9 h-9 rounded-xl flex items-center justify-center shrink-0',
                  item.danger ? 'bg-destructive/10' : 'bg-secondary',
                )}
              >
                <Icon
                  className={cn('w-4 h-4', item.danger ? 'text-destructive' : 'text-primary')}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    'font-medium text-sm',
                    item.danger ? 'text-destructive' : 'text-foreground',
                  )}
                >
                  {item.label}
                </p>
                {item.sublabel && (
                  <p className="text-xs text-muted-foreground truncate">{item.sublabel}</p>
                )}
              </div>
              <ChevronLeft className="w-4 h-4 text-muted-foreground/50 shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Login prompt ─────────────────────────────────────────────────────────────

function LoginPromptCard() {
  const navigate = useNavigate();
  return (
    <div className="mx-4 mb-4 bg-card rounded-2xl border border-border p-6 flex flex-col items-center text-center">
      <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mb-3">
        <User className="w-8 h-8 text-primary" />
      </div>
      <h3 className="font-bold text-foreground text-base mb-1">سجّل الدخول إلى حسابك</h3>
      <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
        سجّل الدخول للوصول إلى محفظة الكاش باك ومتابعة كود الإحالة الخاص بك
      </p>
      <Button
        className="w-full rounded-xl h-11 font-semibold"
        onClick={() => navigate('/login')}
      >
        تسجيل الدخول
      </Button>
      <button
        className="mt-3 text-primary text-sm font-medium"
        onClick={() => navigate('/signup')}
      >
        إنشاء حساب جديد
      </button>
    </div>
  );
}

// ─── User info card ──────────────────────────────────────────────────────────

function UserInfoCard({ displayName, identity }: { displayName: string; identity: string }) {
  return (
    <div className="mx-4 mb-4 bg-card rounded-2xl border border-border p-4 flex items-center gap-3">
      <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
        <User className="w-6 h-6 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground truncate">{displayName}</p>
        <p className="text-sm text-muted-foreground truncate">{identity}</p>
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function Account() {
  const navigate = useNavigate();
  const { setShowCountryPicker, selectedCountry } = useApp();
  const { user, signOut } = useUserAuth();
  const { countries } = useActiveCountries();
  const [storeSheetOpen, setStoreSheetOpen] = useState(false);

  const currentCountry = countries.find((c) => c.id === selectedCountry);
  const countryItems: MenuItem[] = [
    {
      icon: Globe,
      label: 'الدولة',
      sublabel: currentCountry
        ? currentCountry.nameAr
        : 'اختر بلدك لعرض الكوبونات',
      action: () => setShowCountryPicker(true),
    },
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const supportItems: MenuItem[] = [
    {
      icon: MessageSquare,
      label: 'طلب متجر جديد',
      sublabel: 'اقترح متجراً لإضافته',
      action: () => setStoreSheetOpen(true),
    },
  ];

  const cashbackItems: MenuItem[] = [
    {
      icon: Wallet,
      label: 'الكاش باك',
      sublabel: 'عرض الرصيد والسجل والإحالة',
      action: () => navigate('/cashback'),
    },
  ];

  const userDisplayName = user?.displayName || 'مستخدم';
  const userIdentity = user?.email || user?.phoneNumber || '';

  return (
    <div className="min-h-screen">
      {/* Page header */}
      <div className="flex items-center justify-between gap-3 px-4 pt-12 pb-5">
        <h1 className="text-2xl font-bold text-foreground">حسابي</h1>
        <button
          onClick={() => navigate('/')}
          className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 active:opacity-90 transition-opacity ring-2 ring-white/20 shadow-sm"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>

      {/* ── Logged-in state ── */}
      {user ? (
        <>
          {/* User info */}
          <UserInfoCard displayName={userDisplayName} identity={userIdentity} />

          {/* Country selector */}
          <AccountSection title="" items={countryItems} hideTitle />

          {/* Cashback entry point only */}
          <AccountSection title="الكاش باك" items={cashbackItems} hideTitle />

          {/* Support section — no title */}
          <AccountSection title="الدعم" items={supportItems} hideTitle />

          {/* Logout — with confirmation dialog */}
          <div className="px-4 mb-8 mt-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="flex items-center gap-3 w-full bg-card border border-border rounded-2xl p-4 text-right active:bg-secondary/60 transition-colors">
                  <div className="w-9 h-9 rounded-xl bg-destructive/10 flex items-center justify-center shrink-0">
                    <LogOut className="w-4 h-4 text-destructive" />
                  </div>
                  <span className="flex-1 font-medium text-sm text-destructive">
                    تسجيل الخروج
                  </span>
                </button>
              </AlertDialogTrigger>

              <AlertDialogContent dir="rtl">
                <AlertDialogHeader>
                  <AlertDialogTitle>تأكيد تسجيل الخروج</AlertDialogTitle>
                  <AlertDialogDescription>
                    هل أنت متأكد من رغبتك في تسجيل الخروج من حسابك؟
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex-row-reverse gap-2">
                  <AlertDialogCancel className="flex-1">إلغاء</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleSignOut}
                    className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                  >
                    تسجيل الخروج
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </>
      ) : (
        <>
          {/* ── Logged-out state ── */}
          <LoginPromptCard />

          {/* Country selector — available for all users */}
          <AccountSection title="" items={countryItems} hideTitle />

          <AccountSection title="الكاش باك" items={cashbackItems} hideTitle />

          <AccountSection title="الدعم" items={supportItems} hideTitle />
        </>
      )}

      <StoreRequestSheet open={storeSheetOpen} onOpenChange={setStoreSheetOpen} />
    </div>
  );
}
